import React, { useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useAnnouncements, useUniversalAnnouncements } from '../../hooks/useAnnouncements';
import { PulseSeverity } from '../../types';
import { AnnouncementCard } from './AnnouncementCard';
import { Badge } from '../ui/Badge';
import { ChevronDown, ChevronUp, Megaphone, Globe, Filter } from 'lucide-react';
import { cn } from '../../lib/utils';

export const PulseStack = () => {
  const { node, isUniversal, isGlobal } = useTenant();
  const { memberships, isSignedIn } = useCurrentUser();
  
  const nodeAnnouncements = useAnnouncements(node?.id || '', true);
  const universalAnnouncements = useUniversalAnnouncements(isSignedIn && memberships.length > 0);
  
  const { data: rawAnnouncements = [], isLoading } = (isUniversal || isGlobal) ? universalAnnouncements : nodeAnnouncements;
  
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const announcements = selectedNodeId 
    ? rawAnnouncements.filter(a => a.nodeId === selectedNodeId)
    : rawAnnouncements;

  if (isLoading || rawAnnouncements.length === 0) return null;

  const urgent = announcements.filter(a => a.severity === PulseSeverity.CRITICAL);
  const important = announcements.filter(a => a.severity === PulseSeverity.HIGH);
  const other = announcements.filter(a => a.severity === PulseSeverity.MEDIUM || a.severity === PulseSeverity.LOW);

  const totalOtherCount = other.length;

  return (
    <div className="space-y-4 mb-8">
      {isUniversal && (
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-gray-400" />
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Unified Feed</span>
            </div>
            {selectedNodeId && (
              <button 
                onClick={() => setSelectedNodeId(null)}
                className="text-[10px] font-bold text-brand-primary uppercase tracking-widest hover:underline"
              >
                Clear Filter
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            <button
              onClick={() => setSelectedNodeId(null)}
              className={cn(
                "px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all",
                !selectedNodeId 
                  ? "bg-brand-primary text-white shadow-md shadow-brand-primary/20" 
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-slate-800"
              )}
            >
              All My Orgs
            </button>
            {memberships.map((membership) => (
              <button
                key={membership.id}
                onClick={() => setSelectedNodeId(membership.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all",
                  selectedNodeId === membership.id
                    ? "bg-brand-primary text-white shadow-md shadow-brand-primary/20"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-slate-800"
                )}
              >
                {membership.nodeName}
              </button>
            ))}
          </div>
        </div>
      )}

      {announcements.length === 0 && selectedNodeId && (
        <div className="p-8 bg-gray-50 rounded-2xl border border-dashed border-gray-200 dark:border-slate-800 text-center dark:bg-slate-950">
          <Megaphone size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">No announcements found for this organization.</p>
          <button 
            onClick={() => setSelectedNodeId(null)}
            className="text-xs text-brand-primary font-bold mt-2 hover:underline"
          >
            Show everyone again
          </button>
        </div>
      )}

      {/* Urgent always at top, full width */}
      {urgent.map(ann => (
        <AnnouncementCard key={ann.id} announcement={ann} />
      ))}

      {/* Important announcements - Collapsed on mobile, visible on desktop */}
      <div className="md:block hidden space-y-4">
        {important.map(ann => (
          <AnnouncementCard key={ann.id} announcement={ann} />
        ))}
      </div>

      {/* Mobile Important Stack */}
      {important.length > 0 && (
        <div className="md:hidden space-y-3">
          {!isExpanded ? (
            <button
              onClick={() => setIsExpanded(true)}
              className="w-full flex items-center justify-between p-4 bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-900/30 rounded-xl shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-lg">
                  <Megaphone size={18} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-amber-900">{important.length} Important Updates</p>
                  <p className="text-xs text-amber-700">Tap to expand</p>
                </div>
              </div>
              <Badge variant="amber" className="bg-amber-200">{important.length}</Badge>
            </button>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Important Updates</h4>
                <button onClick={() => setIsExpanded(false)} className="text-xs text-blue-600 font-semibold">Collapse</button>
              </div>
              {important.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Collapsed badge for others (Normal/Info) */}
      {totalOtherCount > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 dark:border-slate-700 transition-colors shadow-sm dark:bg-slate-900"
          >
            <Megaphone size={14} className="text-blue-600" />
            <span>{totalOtherCount} more announcements</span>
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {isExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
              {other.map(ann => (
                <AnnouncementCard key={ann.id} announcement={ann} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
